import {
  RouteRecordRaw,
  createMemoryHistory, createRouter,
} from 'vue-router';

import {
  ERouteName,
} from './route-name';

import {
  LOGIN_PATH,
} from '@/router/constants.ts';
import {
  EAppId,
} from '@/types/app-type';
import {
  EAppPermission,
} from '@/utils/permission.ts';

export const ANONYMOUS_ROUTES: ERouteName[] = [
  ERouteName.PLAYGROUND,
];

export const AUTH_ROUTES: ERouteName[] = [
  ERouteName.LOGIN,
];

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: ERouteName.HOME,
    component: () => import('@/page/home/home.vue'),
    meta: {
      userManualPage: 'applications/home.md',
      titleI18nKey: 'page.home.title',
      permissionList: [],
    },
  },
  {
    path: LOGIN_PATH,
    name: ERouteName.LOGIN,
    component: () => import('@/page/login/login.vue'),
    meta: {
      userManualPage: 'login.md',
      titleI18nKey: 'page.login.title',
      permissionList: [],
    },
  },
  {
    path: '/app-overview',
    name: ERouteName.APP_OVERVIEW,
    component: () => import('@/page/app-overview/app-overview.vue'),
    meta: {
      userManualPage: 'settings/general-settings/app-overview.md',
      titleI18nKey: 'page.appOverview.title',
      permissionList: [],
    },
  },
  {
    path: '/certification',
    name: ERouteName.CERTIFICATION,
    component: () => import('@/page/certification/certification.vue'),
    redirect: () => ({
      name: ERouteName.CERTIFICATION_AUDIT,
    }),
    meta: {
      userManualPage: 'applications/audit-quality/index.md',
      appId: EAppId.CERTIFICATION,
      titleI18nKey: `api.AppId.${EAppId.CERTIFICATION}`,
      permissionList: [
        {
          appId: EAppId.CERTIFICATION,
          permission: EAppPermission.READ,
        },
      ],
    },
    children: [
      {
        path: 'audit',
        name: ERouteName.CERTIFICATION_AUDIT,
        component: () => import('@/page/certification/audit/audit.vue'),
        meta: {
          userManualPage: 'applications/audit-quality/audit.md',
          appId: EAppId.CERTIFICATION,
          titleI18nKey: `api.AppId.${EAppId.CERTIFICATION}`,
          permissionList: [
            {
              appId: EAppId.CERTIFICATION,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'audit-configuration',
        name: ERouteName.CERTIFICATION_AUDIT_CONFIGURATION,
        component: () => import('@/page/certification/audit-configuration/audit-configuration.vue'),
        meta: {
          userManualPage: 'applications/audit-quality/audit.md',
          appId: EAppId.CERTIFICATION,
          titleI18nKey: `api.AppId.${EAppId.CERTIFICATION}`,
          permissionList: [
            {
              appId: EAppId.CERTIFICATION,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'quality',
        name: ERouteName.CERTIFICATION_QUALITY,
        component: () => import('@/page/certification/quality/quality.vue'),
        meta: {
          userManualPage: 'applications/audit-quality/quality.md',
          appId: EAppId.CERTIFICATION,
          titleI18nKey: `api.AppId.${EAppId.CERTIFICATION}`,
          permissionList: [
            {
              appId: EAppId.CERTIFICATION,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'quality-statistics',
        name: ERouteName.CERTIFICATION_QUALITY_STATISTICS,
        component: () => import('@/page/certification/quality-statistics/quality-statistics.vue'),
        meta: {
          userManualPage: 'applications/audit-quality/quality.md',
          appId: EAppId.CERTIFICATION,
          titleI18nKey: `api.AppId.${EAppId.CERTIFICATION}`,
          permissionList: [
            {
              appId: EAppId.CERTIFICATION,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'quality-configuration',
        name: ERouteName.CERTIFICATION_QUALITY_CONFIGURATION,
        component: () => import('@/page/certification/quality-configuration/quality-configuration.vue'),
        meta: {
          userManualPage: 'applications/audit-quality/quality.md',
          appId: EAppId.CERTIFICATION,
          titleI18nKey: `api.AppId.${EAppId.CERTIFICATION}`,
          permissionList: [
            {
              appId: EAppId.CERTIFICATION,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
    ],
  },
  {
    path: '/audit/result/:auditId',
    name: ERouteName.CERTIFICATION_AUDIT_RESULT,
    component: () => import('@/page/audit/result.vue'),
    meta: {
      userManualPage: 'applications/audit-quality/audit.md',
      appId: EAppId.CERTIFICATION,
      titleI18nKey: `api.AppId.${EAppId.CERTIFICATION}`,
      permissionList: [
        {
          appId: EAppId.CERTIFICATION,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/quality/result/:reportId',
    name: ERouteName.CERTIFICATION_QUALITY_RESULT,
    component: () => import('@/page/quality/result.vue'),
    meta: {
      userManualPage: 'applications/audit-quality/quality.md',
      appId: EAppId.CERTIFICATION,
      titleI18nKey: `api.AppId.${EAppId.CERTIFICATION}`,
      permissionList: [
        {
          appId: EAppId.CERTIFICATION,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/audit/draft',
    component: () => import('@/page/audit/draft.vue'),
    name: ERouteName.CERTIFICATION_AUDIT_DRAFT,
    meta: {
      userManualPage: 'applications/audit-quality/audit.md',
      appId: EAppId.CERTIFICATION,
      titleI18nKey: `api.AppId.${EAppId.CERTIFICATION}`,
      permissionList: [
        {
          appId: EAppId.CERTIFICATION,
          permission: EAppPermission.READ,
        },
        {
          appId: EAppId.CERTIFICATION,
          permission: EAppPermission.WRITE,
        },
      ],
    },
  },
  {
    path: '/quality/draft',
    name: ERouteName.CERTIFICATION_QUALITY_DRAFT,
    component: () => import('@/page/quality/draft.vue'),
    meta: {
      userManualPage: 'applications/audit-quality/quality.md',
      appId: EAppId.CERTIFICATION,
      titleI18nKey: `api.AppId.${EAppId.CERTIFICATION}`,
      permissionList: [
        {
          appId: EAppId.CERTIFICATION,
          permission: EAppPermission.READ,
        },
        {
          appId: EAppId.CERTIFICATION,
          permission: EAppPermission.WRITE,
        },
      ],
    },
  },
  {
    path: '/grafana',
    name: ERouteName.GRAFANA,
    component: () => import('@/page/grafana/grafana.vue'),
    meta: {
      userManualPage: 'applications/grafana/index.md',
      appId: EAppId.GRAFANA,
      titleI18nKey: `api.AppId.${EAppId.GRAFANA}`,
      permissionList: [
        {
          appId: EAppId.GRAFANA,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/ai-assistant',
    name: ERouteName.AI_ASSISTANT,
    component: () => import('@/page/ai-assistant/ai-assistant.vue'),
    meta: {
      appId: EAppId.AI_ASSISTANT,
      titleI18nKey: `api.AppId.${EAppId.AI_ASSISTANT}`,
      permissionList: [
        {
          appId: EAppId.AI_ASSISTANT,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/model-builder',
    name: ERouteName.MODEL_BUILDER,
    component: () => import('@/page/model-builder/model-builder.vue'),
    meta: {
      userManualPage: 'applications/model-builder/index.md',
      appId: EAppId.MODEL_BUILDER,
      titleI18nKey: `api.AppId.${EAppId.MODEL_BUILDER}`,
      permissionList: [
        {
          appId: EAppId.MODEL_BUILDER,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/model-builder/training',
    name: ERouteName.MODEL_BUILDER_MODEL_TRAINING,
    component: () => import('@/page/model-builder/model-builder-training.vue'),
    meta: {
      userManualPage: 'applications/model-builder/index.md',
      appId: EAppId.MODEL_BUILDER,
      titleI18nKey: `api.AppId.${EAppId.MODEL_BUILDER}`,
      permissionList: [
        {
          appId: EAppId.MODEL_BUILDER,
          permission: EAppPermission.WRITE,
        },
      ],
    },
  },
  {
    path: '/model-builder/:modelId/:modelRevision',
    name: ERouteName.MODEL_BUILDER_MODEL_DETAILS,
    component: () => import('@/page/model-builder/model-builder-details.vue'),
    meta: {
      userManualPage: 'applications/model-builder/index.md',
      appId: EAppId.MODEL_BUILDER,
      titleI18nKey: `api.AppId.${EAppId.MODEL_BUILDER}`,
      permissionList: [
        {
          appId: EAppId.MODEL_BUILDER,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/live-app',
    name: ERouteName.LIVE_APP,
    component: () => import('@/page/live-app/live-app.vue'),
    meta: {
      userManualPage: 'applications/live-app/index.md',
      appId: EAppId.LIVE,
      titleI18nKey: `api.AppId.${EAppId.LIVE}`,
      permissionList: [
        {
          appId: EAppId.LIVE,
          permission: EAppPermission.READ,
        },
      ],
    },
    children: [
      {
        path: 'overview/:assetId',
        name: ERouteName.LIVE_APP_OVERVIEW,
        component: () => import('@/page/live-app/live-app-overview.vue'),
        meta: {
          appId: EAppId.LIVE,
          titleI18nKey: `api.AppId.${EAppId.LIVE}`,
          permissionList: [
            {
              appId: EAppId.LIVE,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'details/:assetId',
        name: ERouteName.LIVE_APP_DETAILS,
        component: () => import('@/page/live-app/live-app-details.vue'),
        meta: {
          appId: EAppId.LIVE,
          titleI18nKey: `api.AppId.${EAppId.LIVE}`,
          permissionList: [
            {
              appId: EAppId.LIVE,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
    ],
  },
  {
    path: '/recipe-builder',
    name: ERouteName.RECIPE_BUILDER,
    component: () => import('@/page/recipe-builder/recipe-builder.vue'),
    meta: {
      userManualPage: 'applications/recipe-builder/index.md',
      appId: EAppId.RECIPE,
      titleI18nKey: `api.AppId.${EAppId.RECIPE}`,
      permissionList: [
        {
          appId: EAppId.RECIPE,
          permission: EAppPermission.READ,
        },
      ],
    },
    children: [
      {
        path: ':assetId',
        name: ERouteName.RECIPE_BUILDER_ASSET,
        component: () => import('@/page/recipe-builder/recipe-builder-asset.vue'),
        meta: {
          appId: EAppId.RECIPE,
          titleI18nKey: `api.AppId.${EAppId.RECIPE}`,
          permissionList: [
            {
              appId: EAppId.RECIPE,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: ':assetId/npm',
        name: ERouteName.RECIPE_BUILDER_NPM_OVERVIEW,
        component: () => import('@/page/recipe-builder/components/npm/recipe-builder-npm-overview.vue'),
        meta: {
          userManualPage: 'applications/recipe-builder/nitride.md',
          appId: EAppId.RECIPE,
          titleI18nKey: `api.AppId.${EAppId.RECIPE}`,
          permissionList: [
            {
              appId: EAppId.RECIPE,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: ':assetId/npm/recipe/:recipeCode/:mode(read|edit)',
        name: ERouteName.RECIPE_BUILDER_NPM_RECIPE_DETAILS_EDIT,
        component: () => import('@/page/recipe-builder/components/npm/recipe-builder-npm-detail.vue'),
        meta: {
          userManualPage: 'applications/recipe-builder/nitride.md',
          appId: EAppId.RECIPE,
          titleI18nKey: `api.AppId.${EAppId.RECIPE}`,
          requiresTile: 'RECIPE_BUILDER',
          additionalAppBarType: 'EQUIPMENT',
          permissionList: [
            {
              appId: EAppId.RECIPE,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: ':assetId/npm/recipe/create',
        name: ERouteName.RECIPE_BUILDER_NPM_RECIPE_CREATE,
        component: () => import('@/page/recipe-builder/components/npm/recipe-builder-npm-detail.vue'),
        meta: {
          userManualPage: 'applications/recipe-builder/nitride.md',
          appId: EAppId.RECIPE,
          titleI18nKey: `api.AppId.${EAppId.RECIPE}`,
          requiresTile: 'RECIPE_BUILDER',
          additionalAppBarType: 'EQUIPMENT',
          permissionList: [
            {
              appId: EAppId.RECIPE,
              permission: EAppPermission.WRITE,
            },
            {
              appId: EAppId.RECIPE,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: ':assetId/pt9800/:ptDbId/automat/:automatNo/process-flow',
        name: ERouteName.RECIPE_BUILDER_PT9800_PROCESS_FLOW_LIST,
        component: () => import('@/page/process-flow/list.vue'),
        meta: {
          userManualPage: 'applications/recipe-builder/process-flows.md',
          appId: EAppId.RECIPE,
          titleI18nKey: `api.AppId.${EAppId.RECIPE}`,
          permissionList: [
            {
              appId: EAppId.RECIPE,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: ':assetId/pt9800/:ptDbId/automat/:automatNo/process-flow/:processFlowId',
        name: ERouteName.RECIPE_BUILDER_PT9800_PROCESS_FLOW_DETAILS,
        component: () => import('@/page/process-flow/process-flow.vue'),
        meta: {
          userManualPage: 'applications/recipe-builder/process-flows.md',
          appId: EAppId.RECIPE,
          titleI18nKey: `api.AppId.${EAppId.RECIPE}`,
          permissionList: [
            {
              appId: EAppId.RECIPE,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: ':assetId/pt9800/:ptDbId/automat/:automatNo/process-flow/:processFlowId/edit',
        name: ERouteName.RECIPE_BUILDER_PT9800_PROCESS_FLOW_EDIT,
        component: () => import('@/page/process-flow/process-flow.vue'),
        meta: {
          userManualPage: 'applications/recipe-builder/process-flows.md',
          appId: EAppId.RECIPE,
          titleI18nKey: `api.AppId.${EAppId.RECIPE}`,
          permissionList: [
            {
              appId: EAppId.RECIPE,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: ':assetId/pt9800/:ptDbId/automat/:automatNo/process-flow/create',
        name: ERouteName.RECIPE_BUILDER_PT9800_PROCESS_FLOW_CREATE,
        component: () => import('@/page/process-flow/process-flow.vue'),
        meta: {
          userManualPage: 'applications/recipe-builder/process-flows.md',
          appId: EAppId.RECIPE,
          titleI18nKey: `api.AppId.${EAppId.RECIPE}`,
          permissionList: [
            {
              appId: EAppId.RECIPE,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: ':assetId/pt9800/:ptDbId/:workplaceId/:recipePrefix',
        name: ERouteName.RECIPE_BUILDER_PT9800_DETAILS,
        component: () => import('@/page/pt9800-recipe/details.vue'),
        meta: {
          userManualPage: 'applications/recipe-builder/protherm.md',
          appId: EAppId.RECIPE,
          titleI18nKey: `api.AppId.${EAppId.RECIPE}`,
          permissionList: [
            {
              appId: EAppId.RECIPE,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: ':assetId/pt9800/:ptDbId/:workplaceId/:recipePrefix/recipe/:recipeTemplateId/:mode(read|edit)',
        name: ERouteName.RECIPE_BUILDER_PT9800_RECIPE_DETAILS_EDIT,
        component: () => import('@/page/pt9800-recipe/recipe-details-edit.vue'),
        meta: {
          userManualPage: 'applications/recipe-builder/protherm.md',
          appId: EAppId.RECIPE,
          titleI18nKey: `api.AppId.${EAppId.RECIPE}`,
          permissionList: [
            {
              appId: EAppId.RECIPE,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: ':assetId/pt9800/:ptDbId/:workplaceId/:recipePrefix/recipe/create',
        name: ERouteName.RECIPE_BUILDER_PT9800_RECIPE_CREATE,
        component: () => import('@/page/pt9800-recipe/recipe-create.vue'),
        meta: {
          userManualPage: 'applications/recipe-builder/protherm.md',
          appId: EAppId.RECIPE,
          titleI18nKey: `api.AppId.${EAppId.RECIPE}`,
          permissionList: [
            {
              appId: EAppId.RECIPE,
              permission: EAppPermission.WRITE,
            },
            {
              appId: EAppId.RECIPE,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
    ],
  },
  {
    path: '/upc-pro',
    name: ERouteName.UPC_PRO,
    redirect: '/upc-pro/carbon-oxygen',
    component: () => import('@/views/upcPro/index.vue'),
    meta: {
      userManualPage: 'applications/upc-pro/index.md',
      appId: EAppId.UPC_PRO,
      titleI18nKey: `api.AppId.${EAppId.UPC_PRO}`,
      requiresTile: 'UPC_PRO',
      additionalAppBarType: 'UPC_PRO',
      permissionList: [
        {
          appId: EAppId.UPC_PRO,
          permission: EAppPermission.READ,
        },
      ],
    },
    children: [
      {
        path: 'carbon-oxygen',
        name: ERouteName.UPC_PRO_CARBON_OXYGEN,
        component: () => import('@/views/upcPro/CarbonOxygen.vue'),
        meta: {
          appId: EAppId.UPC_PRO,
          titleI18nKey: `api.AppId.${EAppId.UPC_PRO}`,
          permissionList: [
            {
              appId: EAppId.UPC_PRO,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'nitrogen-methanol',
        name: 'nitrogen-methanol',
        component: () => import('@/views/upcPro/NitrogenMethanol.vue'),
        meta: {
          appId: EAppId.UPC_PRO,
          titleI18nKey: `api.AppId.${EAppId.UPC_PRO}`,
          permissionList: [
            {
              appId: EAppId.UPC_PRO,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'endo-generator',
        name: 'endo-generator',
        component: () => import('@/views/upcPro/EndoGenerator.vue'),
        meta: {
          appId: EAppId.UPC_PRO,
          titleI18nKey: `api.AppId.${EAppId.UPC_PRO}`,
          permissionList: [
            {
              appId: EAppId.UPC_PRO,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'tc-conversion',
        name: 'tc-conversion',
        component: () => import('@/views/upcPro/TCConversion.vue'),
        meta: {
          appId: EAppId.UPC_PRO,
          titleI18nKey: `api.AppId.${EAppId.UPC_PRO}`,
          permissionList: [
            {
              appId: EAppId.UPC_PRO,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'shim',
        name: 'shim',
        component: () => import('@/views/upcPro/Shim.vue'),
        meta: {
          appId: EAppId.UPC_PRO,
          titleI18nKey: `api.AppId.${EAppId.UPC_PRO}`,
          permissionList: [
            {
              appId: EAppId.UPC_PRO,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'nitriding',
        name: 'nitriding',
        component: () => import('@/views/upcPro/Nitriding.vue'),
        meta: {
          appId: EAppId.UPC_PRO,
          titleI18nKey: `api.AppId.${EAppId.UPC_PRO}`,
          permissionList: [
            {
              appId: EAppId.UPC_PRO,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'rtds',
        name: 'rtds',
        component: () => import('@/views/upcPro/RTDs.vue'),
        meta: {
          appId: EAppId.UPC_PRO,
          titleI18nKey: `api.AppId.${EAppId.UPC_PRO}`,
          permissionList: [
            {
              appId: EAppId.UPC_PRO,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'converters',
        name: 'converters',
        component: () => import('@/views/upcPro/Converters.vue'),
        meta: {
          appId: EAppId.UPC_PRO,
          titleI18nKey: `api.AppId.${EAppId.UPC_PRO}`,
          permissionList: [
            {
              appId: EAppId.UPC_PRO,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'alloy-factor',
        name: 'alloy-factor',
        component: () => import('@/views/upcPro/AlloyFactor.vue'),
        meta: {
          appId: EAppId.UPC_PRO,
          titleI18nKey: `api.AppId.${EAppId.UPC_PRO}`,
          permissionList: [
            {
              appId: EAppId.UPC_PRO,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'flo-meter',
        name: 'flo-meter',
        component: () => import('@/views/upcPro/FloMeter.vue'),
        meta: {
          appId: EAppId.UPC_PRO,
          titleI18nKey: `api.AppId.${EAppId.UPC_PRO}`,
          permissionList: [
            {
              appId: EAppId.UPC_PRO,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
    ],
  },
  {
    path: '/asset-management',
    name: ERouteName.ASSET_MANAGEMENT_OVERVIEW,
    component: () => import('@/page/asset-management/overview.vue'),
    meta: {
      userManualPage: 'applications/asset-management/index.md',
      appId: EAppId.ASSET_MANAGEMENT,
      titleI18nKey: `api.AppId.${EAppId.ASSET_MANAGEMENT}`,
      permissionList: [
        {
          appId: EAppId.ASSET_MANAGEMENT,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  // ERouteName.ASSET_MANAGEMENT_OVERVIEW should go before to override "/asset-management" path
  {
    path: '/asset-management',
    component: () => import('@/page/asset-management/sidebar-layout.vue'),
    name: ERouteName.ASSET_MANAGEMENT_OVERVIEW_DUMMY,
    meta: {
      userManualPage: 'applications/asset-management/index.md',
      appId: EAppId.ASSET_MANAGEMENT,
      titleI18nKey: `api.AppId.${EAppId.ASSET_MANAGEMENT}`,
      permissionList: [
        {
          appId: EAppId.ASSET_MANAGEMENT,
          permission: EAppPermission.READ,
        },
      ],
    },
    children: [
      {
        path: '/asset-management/:assetHierarchyLevel/:assetId/main-equipment',
        name: ERouteName.ASSET_MANAGEMENT_MAIN_EQUIPMENT,
        redirect: (to) => to.fullPath.replace('/main-equipment', '/main'),
      },
      {
        path: '/asset-management/:assetHierarchyLevel/:assetId/main',
        name: ERouteName.ASSET_MANAGEMENT_MAIN,
        component: () => import('@/page/asset-management/asset-main.vue'),
        meta: {
          appId: EAppId.ASSET_MANAGEMENT,
          titleI18nKey: `api.AppId.${EAppId.ASSET_MANAGEMENT}`,
          permissionList: [
            {
              appId: EAppId.ASSET_MANAGEMENT,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: '/asset-management/:assetHierarchyLevel/:assetId/documents',
        name: ERouteName.ASSET_MANAGEMENT_DOCUMENT,
        component: () => import('@/page/asset-management/document-list.vue'),
        meta: {
          appId: EAppId.ASSET_MANAGEMENT,
          titleI18nKey: `api.AppId.${EAppId.ASSET_MANAGEMENT}`,
          permissionList: [
            {
              appId: EAppId.ASSET_MANAGEMENT,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: '/asset-management/:assetHierarchyLevel/:assetId/parameter',
        name: ERouteName.ASSET_MANAGEMENT_PARAMETER,
        component: () => import('@/page/asset-management/asset-parameter.vue'),
        meta: {
          appId: EAppId.ASSET_MANAGEMENT,
          titleI18nKey: `api.AppId.${EAppId.ASSET_MANAGEMENT}`,
          permissionList: [
            {
              appId: EAppId.ASSET_MANAGEMENT,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: '/asset-management/:assetHierarchyLevel/:assetId/sensor-settings',
        name: ERouteName.ASSET_MANAGEMENT_SENSOR_SETTINGS,
        component: () => import('@/page/asset-management/sensor-settings.vue'),
        meta: {
          appId: EAppId.ASSET_MANAGEMENT,
          titleI18nKey: `api.AppId.${EAppId.ASSET_MANAGEMENT}`,
          permissionList: [
            {
              appId: EAppId.ASSET_MANAGEMENT,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: '/asset-management/:assetHierarchyLevel/:assetId/sensor-mapping',
        name: ERouteName.ASSET_MANAGEMENT_SENSOR_MAPPING,
        component: () => import('@/page/asset-management/sensor-mapping.vue'),
        meta: {
          appId: EAppId.ASSET_MANAGEMENT,
          titleI18nKey: `api.AppId.${EAppId.ASSET_MANAGEMENT}`,
          permissionList: [
            {
              appId: EAppId.ASSET_MANAGEMENT,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: '/asset-management/:assetHierarchyLevel/:assetId/data-sources',
        name: ERouteName.ASSET_MANAGEMENT_DATA_SOURCES,
        component: () => import('@/page/asset-management/components/data-sources.vue'),
        meta: {
          appId: EAppId.ASSET_MANAGEMENT,
          titleI18nKey: `api.AppId.${EAppId.ASSET_MANAGEMENT}`,
          permissionList: [
            {
              appId: EAppId.ASSET_MANAGEMENT,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: '/asset-management/:assetHierarchyLevel/:assetId/cameras',
        name: ERouteName.ASSET_MANAGEMENT_CAMERAS,
        component: () => import('@/page/asset-management/cameras.vue'),
        meta: {
          appId: EAppId.ASSET_MANAGEMENT,
          titleI18nKey: `api.AppId.${EAppId.ASSET_MANAGEMENT}`,
          permissionList: [
            {
              appId: EAppId.ASSET_MANAGEMENT,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: '/asset-management/:assetHierarchyLevel/:assetId/alarm-management',
        name: ERouteName.ASSET_MANAGEMENT_ALARM_MANAGEMENT,
        component: () => import('@/page/asset-management/alarm-management.vue'),
        meta: {
          appId: EAppId.ASSET_MANAGEMENT,
          titleI18nKey: `api.AppId.${EAppId.ASSET_MANAGEMENT}`,
          permissionList: [
            {
              appId: EAppId.ASSET_MANAGEMENT,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
    ],
  },
  {
    path: '/operation-calendar',
    name: ERouteName.OPERATION_CALENDAR,
    component: () => import('@/page/operation-calendar/operation-calendar.vue'),
    redirect: () => ({
      name: ERouteName.OPERATION_CALENDAR_CALENDAR,
    }),
    meta: {
      userManualPage: 'applications/operation-calendar/index.md',
      appId: EAppId.OPERATION_CALENDAR,
      titleI18nKey: `api.AppId.${EAppId.OPERATION_CALENDAR}`,
      permissionList: [
        {
          appId: EAppId.OPERATION_CALENDAR,
          permission: EAppPermission.READ,
        },
      ],
    },
    children: [
      {
        path: 'calendar',
        name: ERouteName.OPERATION_CALENDAR_CALENDAR,
        component: () => import('@/page/operation-calendar/calendar/calendar.vue'),
        redirect: () => ({
          name: ERouteName.OPERATION_CALENDAR_MONTH,
        }),
        meta: {
          appId: EAppId.OPERATION_CALENDAR,
          titleI18nKey: `api.AppId.${EAppId.OPERATION_CALENDAR}`,
          permissionList: [
            {
              appId: EAppId.OPERATION_CALENDAR,
              permission: EAppPermission.READ,
            },
          ],
        },
        children: [
          {
            path: 'month',
            name: ERouteName.OPERATION_CALENDAR_MONTH,
            component: () => import('@/page/operation-calendar/calendar/router-views/router-view-calendar-month.vue'),
            meta: {
              appId: EAppId.OPERATION_CALENDAR,
              titleI18nKey: `api.AppId.${EAppId.OPERATION_CALENDAR}`,
              permissionList: [
                {
                  appId: EAppId.OPERATION_CALENDAR,
                  permission: EAppPermission.READ,
                },
              ],
            },
          },
          {
            path: 'week',
            name: ERouteName.OPERATION_CALENDAR_WEEK,
            component: () => import('@/page/operation-calendar/calendar/router-views/router-view-calendar-week.vue'),
            meta: {
              appId: EAppId.OPERATION_CALENDAR,
              titleI18nKey: `api.AppId.${EAppId.OPERATION_CALENDAR}`,
              permissionList: [
                {
                  appId: EAppId.OPERATION_CALENDAR,
                  permission: EAppPermission.READ,
                },
              ],
            },
          },
          {
            path: 'year',
            name: ERouteName.OPERATION_CALENDAR_YEAR,
            component: () => import('@/page/operation-calendar/calendar/router-views/router-view-calendar-year.vue'),
            meta: {
              appId: EAppId.OPERATION_CALENDAR,
              titleI18nKey: `api.AppId.${EAppId.OPERATION_CALENDAR}`,
              permissionList: [
                {
                  appId: EAppId.OPERATION_CALENDAR,
                  permission: EAppPermission.READ,
                },
              ],
            },
          },
        ],
      },
      {
        path: 'events',
        name: ERouteName.OPERATION_CALENDAR_EVENTS,
        component: () => import('@/page/operation-calendar/events/operation-calendar-events.vue'),
        meta: {
          appId: EAppId.OPERATION_CALENDAR,
          titleI18nKey: `api.AppId.${EAppId.OPERATION_CALENDAR}`,
          permissionList: [
            {
              appId: EAppId.OPERATION_CALENDAR,
              permission: EAppPermission.READ,
            },
          ],
        },
        children: [
          {
            path: 'maintenance',
            name: ERouteName.OPERATION_CALENDAR_EVENTS_MAINTENANCE,
            component: () => import('@/page/operation-calendar/components/table-events/table-events-maintenance-occurrence.vue'),
            meta: {
              appId: EAppId.OPERATION_CALENDAR,
              titleI18nKey: `api.AppId.${EAppId.OPERATION_CALENDAR}`,
              permissionList: [
                {
                  appId: EAppId.OPERATION_CALENDAR,
                  permission: EAppPermission.READ,
                },
              ],
            },
          },
          {
            path: 'operation',
            name: ERouteName.OPERATION_CALENDAR_EVENTS_OPERATION,
            component: () => import('@/page/operation-calendar/components/table-events/table-events-operation-calendar.vue'),
            meta: {
              appId: EAppId.OPERATION_CALENDAR,
              titleI18nKey: `api.AppId.${EAppId.OPERATION_CALENDAR}`,
              permissionList: [
                {
                  appId: EAppId.OPERATION_CALENDAR,
                  permission: EAppPermission.READ,
                },
              ],
            },
          },
        ],
      },
      {
        path: 'tags/event',
        name: ERouteName.OPERATION_CALENDAR_EVENT_TAGS,
        component: () => import('@/page/operation-calendar/event-tags.vue'),
        meta: {
          userManualPage: 'applications/operation-calendar/index.md',
          appId: EAppId.OPERATION_CALENDAR,
          titleI18nKey: `api.AppId.${EAppId.OPERATION_CALENDAR}`,
          permissionList: [
            {
              appId: EAppId.OPERATION_CALENDAR,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'events/operation/:eventId',
        name: ERouteName.OPERATION_CALENDAR_EVENTS_OPERATION_DETAILS,
        component: () => import('@/page/operation-calendar/events/operation/operation-calendar-event-details.vue'),
        meta: {
          appId: EAppId.OPERATION_CALENDAR,
          titleI18nKey: `api.AppId.${EAppId.OPERATION_CALENDAR}`,
          permissionList: [
            {
              appId: EAppId.OPERATION_CALENDAR,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'events/maintenance/:maintenanceOccurrenceId',
        name: ERouteName.OPERATION_CALENDAR_EVENTS_MAINTENANCE_DETAILS,
        component: () => import('@/page/operation-calendar/events/maintenance/maintenance-occurrence-details.vue'),
        meta: {
          userManualPage: 'applications/operation-calendar/index.md',
          appId: EAppId.OPERATION_CALENDAR,
          titleI18nKey: `api.AppId.${EAppId.OPERATION_CALENDAR}`,
          permissionList: [
            {
              appId: EAppId.OPERATION_CALENDAR,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
    ],
  },
  {
    path: '/production-logs',
    name: ERouteName.PRODUCTION_LOGS,
    component: () => import('@/page/production-logs/production-logs.vue'),
    meta: {
      userManualPage: 'applications/production-logs/index.md',
      appId: EAppId.PRODUCTION_LOGS,
      titleI18nKey: `api.AppId.${EAppId.PRODUCTION_LOGS}`,
      permissionList: [
        {
          appId: EAppId.PRODUCTION_LOGS,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/production-logs/comparison',
    name: ERouteName.PRODUCTION_LOGS_JOB_COMPARISON,
    component: () => import('@/page/production-logs/job-comparison.vue'),
    meta: {
      userManualPage: 'applications/production-logs/index.md',
      appId: EAppId.PRODUCTION_LOGS,
      titleI18nKey: `api.AppId.${EAppId.PRODUCTION_LOGS}`,
      permissionList: [
        {
          appId: EAppId.PRODUCTION_LOGS,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/production-logs/:jobId',
    name: ERouteName.PRODUCTION_LOGS_JOB_DETAILS,
    component: () => import('@/page/production-logs/production-logs-job-detail.vue'),
    meta: {
      userManualPage: 'applications/chart-recorder/index.md',
      appId: EAppId.PRODUCTION_LOGS,
      titleI18nKey: `api.AppId.${EAppId.PRODUCTION_LOGS}`,
      permissionList: [
        {
          appId: EAppId.PRODUCTION_LOGS,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/production-statistics',
    name: ERouteName.PRODUCTION_STATISTICS,
    component: () => import('@/page/production-statistics/production-statistics.vue'),
    meta: {
      userManualPage: 'applications/production-statistics/index.md',
      appId: EAppId.PRODUCTION_STATISTICS,
      titleI18nKey: `api.AppId.${EAppId.PRODUCTION_STATISTICS}`,
      permissionList: [
        {
          appId: EAppId.PRODUCTION_STATISTICS,
          permission: EAppPermission.READ,
        },
      ],
    },
    children: [
      {
        path: ':statisticsLevel/:assetId',
        name: ERouteName.PRODUCTION_STATISTICS_DETAILS,
        component: () => import('@/page/production-statistics/details.vue'),
        meta: {
          appId: EAppId.PRODUCTION_STATISTICS,
          titleI18nKey: `api.AppId.${EAppId.PRODUCTION_STATISTICS}`,
          permissionList: [
            {
              appId: EAppId.PRODUCTION_STATISTICS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        // only for jobs and recipes
        path: ':statisticsLevel/:parentId/:assetId',
        name: ERouteName.PRODUCTION_STATISTICS_DETAILS_JOB_RECIPE,
        component: () => import('@/page/production-statistics/details.vue'),
        meta: {
          appId: EAppId.PRODUCTION_STATISTICS,
          titleI18nKey: `api.AppId.${EAppId.PRODUCTION_STATISTICS}`,
          permissionList: [
            {
              appId: EAppId.PRODUCTION_STATISTICS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
    ],
  },
  {
    path: '/recipe-on-demand',
    name: ERouteName.RECIPE_ON_DEMAND,
    component: () => import('@/views/recipeOnDemand/Recipes.vue'),
    meta: {
      userManualPage: 'applications/recipe-on-demand/index.md',
      appId: EAppId.RECIPE_ON_DEMAND,
      // Required for EquipmentTreeview.vue
      requiresTile: 'RECIPE_ON_DEMAND',
      titleI18nKey: `api.AppId.${EAppId.RECIPE_ON_DEMAND}`,
      permissionList: [
        {
          appId: EAppId.RECIPE_ON_DEMAND,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/recipe-on-demand/:recipeId',
    name: 'recipe-details',
    component: () => import('@/views/recipeOnDemand/RecipesDetails.vue'),
    meta: {
      userManualPage: 'applications/recipe-on-demand/index.md',
      appId: EAppId.RECIPE_ON_DEMAND,
      // Required for EquipmentTreeview.vue
      requiresTile: 'RECIPE_ON_DEMAND',
      titleI18nKey: `api.AppId.${EAppId.RECIPE_ON_DEMAND}`,
      permissionList: [
        {
          appId: EAppId.RECIPE_ON_DEMAND,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/performance-dashboard',
    name: ERouteName.PERFORMANCE_DASHBOARD,
    component: () => import('@/page/performance-dashboard/performance-dashboard.vue'),
    meta: {
      userManualPage: 'applications/performance-dashboard/index.md',
      appId: EAppId.PERFORMANCE_DASHBOARD,
      titleI18nKey: `api.AppId.${EAppId.PERFORMANCE_DASHBOARD}`,
      permissionList: [
        {
          appId: EAppId.PERFORMANCE_DASHBOARD,
          permission: EAppPermission.READ,
        },
      ],
    },
    children: [
      {
        path: ':assetId',
        name: ERouteName.PERFORMANCE_DASHBOARD_DETAILS,
        component: () => import('@/page/performance-dashboard/performance-dashboard-details.vue'),
        meta: {
          appId: EAppId.PERFORMANCE_DASHBOARD,
          titleI18nKey: `api.AppId.${EAppId.PERFORMANCE_DASHBOARD}`,
          permissionList: [
            {
              appId: EAppId.PERFORMANCE_DASHBOARD,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
    ],
  },
  {
    path: '/user-management',
    name: ERouteName.USER_MANAGEMENT,
    component: () => import('@/page/user-management/user-management.vue'),
    meta: {
      userManualPage: 'settings/user-management/index.md',
      appId: EAppId.USER_MANAGEMENT,
      titleI18nKey: `api.AppId.${EAppId.USER_MANAGEMENT}`,
      permissionList: [
        {
          appId: EAppId.USER_MANAGEMENT,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/role-management',
    name: ERouteName.ROLE_MANAGEMENT,
    component: () => import('@/page/user-management/role-management.vue'),
    meta: {
      userManualPage: 'settings/user-management/index.md',
      appId: EAppId.USER_MANAGEMENT,
      titleI18nKey: `api.AppId.${EAppId.USER_MANAGEMENT}`,
      permissionList: [
        {
          appId: EAppId.USER_MANAGEMENT,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/remote-control',
    name: ERouteName.REMOTE_CONTROL,
    component: () => import('@/page/remote-control/remote-control.vue'),
    meta: {
      userManualPage: 'settings/general-settings/remote-control.md',
      appId: EAppId.REMOTE_CONTROL,
      titleI18nKey: `api.AppId.${EAppId.REMOTE_CONTROL}`,
      permissionList: [
        {
          appId: EAppId.REMOTE_CONTROL,
          permission: EAppPermission.READ,
        },
      ],
    },
    children: [
      {
        path: ':assetId',
        name: ERouteName.REMOTE_CONTROL_DETAILS,
        component: () => import('@/page/remote-control/details.vue'),
        meta: {
          appId: EAppId.REMOTE_CONTROL,
          titleI18nKey: `api.AppId.${EAppId.REMOTE_CONTROL}`,
          permissionList: [
            {
              appId: EAppId.REMOTE_CONTROL,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
    ],
  },
  {
    path: '/history-and-trends/:assetId?',
    name: ERouteName.HISTORY_AND_TRENDS,
    component: () => import('@/page/history-and-trends/history-and-trends.vue'),
    meta: {
      userManualPage: 'applications/history-trends/index.md',
      appId: EAppId.HISTORY_AND_TRENDS,
      titleI18nKey: `api.AppId.${EAppId.HISTORY_AND_TRENDS}`,
      permissionList: [
        {
          appId: EAppId.HISTORY_AND_TRENDS,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/shop-layout',
    name: ERouteName.SHOP_LAYOUT,
    component: () => import('@/page/shop-layout/shop-layout.vue'),
    meta: {
      userManualPage: 'applications/shop-layout/index.md',
      appId: EAppId.SHOP_LAYOUT,
      titleI18nKey: `api.AppId.${EAppId.SHOP_LAYOUT}`,
      permissionList: [
        {
          appId: EAppId.SHOP_LAYOUT,
          permission: EAppPermission.READ,
        },
      ],
    },
    children: [
      {
        path: '/pt9800/:ptDbId/shop-layout/:shopLayoutId/automat/:automatNo',
        name: ERouteName.SHOP_LAYOUT_DETAILS,
        component: () => import('@/page/shop-layout/details.vue'),
        meta: {
          appId: EAppId.SHOP_LAYOUT,
          titleI18nKey: `api.AppId.${EAppId.SHOP_LAYOUT}`,
          permissionList: [
            {
              appId: EAppId.SHOP_LAYOUT,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
    ],
  },
  {
    path: '/pt9800/:view?',
    name: ERouteName.PT9800_CONFIGURATION,
    component: () => import('@/page/pt9800-configuration/pt9800-configuration.vue'),
    meta: {
      userManualPage: 'applications/pt9800-cloud/index.md',
      appId: EAppId.PT9800_CONFIGURATION,
      titleI18nKey: `api.AppId.${EAppId.PT9800_CONFIGURATION}`,
      permissionList: [
        {
          appId: EAppId.PT9800_CONFIGURATION,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/alarm-statistics',
    name: ERouteName.ALARM_STATISTICS,
    component: () => import('@/page/alarm-statistics/alarm-statistics.vue'),
    meta: {
      userManualPage: 'applications/alarm-statistics/index.md',
      appId: EAppId.ALARM_STATISTICS,
      titleI18nKey: `api.AppId.${EAppId.ALARM_STATISTICS}`,
      permissionList: [
        {
          appId: EAppId.ALARM_STATISTICS,
          permission: EAppPermission.READ,
        },
      ],
    },
    children: [
      {
        path: 'equipment/:assetId',
        name: ERouteName.ALARM_STATISTICS_EQUIPMENT,
        component: () => import('@/page/alarm-statistics/alarm-statistics-equipment.vue'),
        meta: {
          appId: EAppId.ALARM_STATISTICS,
          titleI18nKey: `api.AppId.${EAppId.ALARM_STATISTICS}`,
          permissionList: [
            {
              appId: EAppId.ALARM_STATISTICS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
    ],
  },
  {
    path: '/customer',
    name: ERouteName.CUSTOMER,
    component: () => import('@/page/customer/customer-list.vue'),
    meta: {
      userManualPage: 'applications/customers/index.md',
      appId: EAppId.CUSTOMER,
      titleI18nKey: `api.AppId.${EAppId.CUSTOMER}`,
      permissionList: [
        {
          appId: EAppId.CUSTOMER,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/customer/:customerId',
    name: ERouteName.CUSTOMER_DETAILS,
    component: () => import('@/page/customer/details.vue'),
    meta: {
      userManualPage: 'applications/customers/index.md',
      appId: EAppId.CUSTOMER,
      titleI18nKey: `api.AppId.${EAppId.CUSTOMER}`,
      permissionList: [
        {
          appId: EAppId.CUSTOMER,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/customer/:customerId/edit',
    name: ERouteName.CUSTOMER_EDIT,
    component: () => import('@/page/customer/edit.vue'),
    meta: {
      userManualPage: 'applications/customers/index.md',
      appId: EAppId.CUSTOMER,
      titleI18nKey: `api.AppId.${EAppId.CUSTOMER}`,
      permissionList: [
        {
          appId: EAppId.CUSTOMER,
          permission: EAppPermission.READ,
        },
        {
          appId: EAppId.CUSTOMER,
          permission: EAppPermission.WRITE,
        },
      ],
    },
  },
  {
    path: '/customer/create',
    name: ERouteName.CUSTOMER_CREATE,
    component: () => import('@/page/customer/create.vue'),
    meta: {
      userManualPage: 'applications/customers/index.md',
      appId: EAppId.CUSTOMER,
      titleI18nKey: `api.AppId.${EAppId.CUSTOMER}`,
      permissionList: [
        {
          appId: EAppId.CUSTOMER,
          permission: EAppPermission.READ,
        },
        {
          appId: EAppId.CUSTOMER,
          permission: EAppPermission.WRITE,
        },
      ],
    },
  },
  {
    path: '/loading-scheme',
    name: ERouteName.LOADING_SCHEME,
    component: () => import('@/page/loading-scheme/loading-scheme.vue'),
    meta: {
      appId: EAppId.PARTS,
      titleI18nKey: `api.AppId.${EAppId.PARTS}`,
      permissionList: [
        {
          appId: EAppId.PARTS,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/loading-scheme/:schemeId',
    name: ERouteName.LOADING_SCHEME_DETAILS,
    component: () => import('@/page/loading-scheme/details.vue'),
    meta: {
      appId: EAppId.PARTS,
      titleI18nKey: `api.AppId.${EAppId.PARTS}`,
      permissionList: [
        {
          appId: EAppId.PARTS,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/loading-scheme/:schemeId/edit',
    name: ERouteName.LOADING_SCHEME_EDIT,
    component: () => import('@/page/loading-scheme/edit.vue'),
    meta: {
      appId: EAppId.PARTS,
      titleI18nKey: `api.AppId.${EAppId.PARTS}`,
      permissionList: [
        {
          appId: EAppId.PARTS,
          permission: EAppPermission.WRITE,
        },
      ],
    },
  },
  {
    path: '/loading-scheme/create',
    name: ERouteName.LOADING_SCHEME_CREATE,
    component: () => import('@/page/loading-scheme/create.vue'),
    meta: {
      appId: EAppId.PARTS,
      titleI18nKey: `api.AppId.${EAppId.PARTS}`,
      permissionList: [
        {
          appId: EAppId.PARTS,
          permission: EAppPermission.WRITE,
        },
      ],
    },
  },
  {
    path: '/parts',
    name: ERouteName.PART,
    component: () => import('@/page/part/part.vue'),
    meta: {
      userManualPage: 'applications/parts/index.md',
      appId: EAppId.PARTS,
      titleI18nKey: `api.AppId.${EAppId.PARTS}`,
      permissionList: [
        {
          appId: EAppId.PARTS,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/parts/:partId/versions/:versionId',
    name: ERouteName.PART_VERSION_DETAILS,
    component: () => import('@/page/part/details.vue'),
    meta: {
      userManualPage: 'applications/parts/index.md',
      appId: EAppId.PARTS,
      titleI18nKey: `api.AppId.${EAppId.PARTS}`,
      permissionList: [
        {
          appId: EAppId.PARTS,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/parts/:partId/versions/:versionId/edit',
    name: ERouteName.PART_VERSION_EDIT,
    component: () => import('@/page/part/edit.vue'),
    meta: {
      userManualPage: 'applications/parts/index.md',
      appId: EAppId.PARTS,
      titleI18nKey: `api.AppId.${EAppId.PARTS}`,
      permissionList: [
        {
          appId: EAppId.PARTS,
          permission: EAppPermission.WRITE,
        },
      ],
    },
  },
  {
    path: '/parts/create',
    name: ERouteName.PART_CREATE,
    component: () => import('@/page/part/create.vue'),
    meta: {
      userManualPage: 'applications/parts/index.md',
      appId: EAppId.PARTS,
      titleI18nKey: `api.AppId.${EAppId.PARTS}`,
      permissionList: [
        {
          appId: EAppId.PARTS,
          permission: EAppPermission.WRITE,
        },
      ],
    },
  },
  {
    path: '/anomaly-detection',
    name: ERouteName.ANOMALY_DETECTION,
    component: () => import('@/page/anomaly-detection/anomaly-detection.vue'),
    meta: {
      userManualPage: 'applications/anomaly-detection/index.md',
      appId: EAppId.ANOMALY_DETECTION,
      titleI18nKey: `api.AppId.${EAppId.ANOMALY_DETECTION}`,
      permissionList: [
        {
          appId: EAppId.ANOMALY_DETECTION,
          permission: EAppPermission.READ,
        },
      ],
    },
    children: [
      {
        path: ':assetId',
        name: ERouteName.ANOMALY_DETECTION_EQUIPMENT,
        component: () => import('@/page/anomaly-detection/equipment.vue'),
        meta: {
          appId: EAppId.ANOMALY_DETECTION,
          titleI18nKey: `api.AppId.${EAppId.ANOMALY_DETECTION}`,
          permissionList: [
            {
              appId: EAppId.ANOMALY_DETECTION,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: ':assetId/settings',
        name: ERouteName.ANOMALY_DETECTION_EQUIPMENT_SETTINGS,
        component: () => import('@/page/anomaly-detection/equipment-settings.vue'),
        meta: {
          appId: EAppId.ANOMALY_DETECTION,
          titleI18nKey: `api.AppId.${EAppId.ANOMALY_DETECTION}`,
          permissionList: [
            {
              appId: EAppId.ANOMALY_DETECTION,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: ':assetId/job-feature/:dataId',
        name: ERouteName.ANOMALY_DETECTION_JOB_FEATURE_DETAILS,
        component: () => import('@/page/anomaly-detection/job-feature-details.vue'),
        meta: {
          appId: EAppId.ANOMALY_DETECTION,
          titleI18nKey: `api.AppId.${EAppId.ANOMALY_DETECTION}`,
          permissionList: [
            {
              appId: EAppId.ANOMALY_DETECTION,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
    ],
  },
  {
    path: '/simulator',
    name: ERouteName.SIMULATOR,
    component: () => import('@/page/simulator/create.vue'),
    meta: {
      userManualPage: 'applications/simulators/index.md',
      appId: EAppId.SIMULATOR,
      titleI18nKey: `api.AppId.${EAppId.SIMULATOR}`,
      permissionList: [
        {
          appId: EAppId.SIMULATOR,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/materials',
    name: ERouteName.MATERIAL_LIST,
    component: () => import('@/page/material/material-list.vue'),
    meta: {
      userManualPage: 'applications/materials/index.md',
      appId: EAppId.MATERIALS,
      titleI18nKey: `api.AppId.${EAppId.MATERIALS}`,
      permissionList: [
        {
          appId: EAppId.MATERIALS,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/materials/groups',
    name: ERouteName.MATERIAL_GROUPS,
    component: () => import('@/page/material/material-group-list.vue'),
    meta: {
      userManualPage: 'applications/materials/index.md',
      appId: EAppId.MATERIALS,
      titleI18nKey: `api.AppId.${EAppId.MATERIALS}`,
      permissionList: [
        {
          appId: EAppId.MATERIALS,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/materials/:materialId',
    name: ERouteName.MATERIAL_DETAILS,
    component: () => import('@/page/material/material-details.vue'),
    meta: {
      userManualPage: 'applications/materials/index.md',
      appId: EAppId.MATERIALS,
      titleI18nKey: `api.AppId.${EAppId.MATERIALS}`,
      permissionList: [
        {
          appId: EAppId.MATERIALS,
          permission: EAppPermission.READ,
        },
      ],
    },
  },
  {
    path: '/materials/:materialId/edit',
    name: ERouteName.MATERIAL_EDIT,
    component: () => import('@/page/material/material-edit.vue'),
    meta: {
      userManualPage: 'applications/materials/index.md',
      appId: EAppId.MATERIALS,
      titleI18nKey: `api.AppId.${EAppId.MATERIALS}`,
      permissionList: [
        {
          appId: EAppId.MATERIALS,
          permission: EAppPermission.READ,
        },
        {
          appId: EAppId.MATERIALS,
          permission: EAppPermission.WRITE,
        },
      ],
    },
  },
  {
    path: '/materials/create',
    name: ERouteName.MATERIAL_CREATE,
    component: () => import('@/page/material/material-create.vue'),
    meta: {
      userManualPage: 'applications/materials/index.md',
      appId: EAppId.MATERIALS,
      titleI18nKey: `api.AppId.${EAppId.MATERIALS}`,
      permissionList: [
        {
          appId: EAppId.MATERIALS,
          permission: EAppPermission.READ,
        },
        {
          appId: EAppId.MATERIALS,
          permission: EAppPermission.WRITE,
        },
      ],
    },
  },
  {
    path: '/notifications',
    name: ERouteName.NOTIFICATION,
    component: () => import('@/page/notification/notification.vue'),
    meta: {
      userManualPage: 'settings/general-settings/notifications.md',
      titleI18nKey: 'page.notification.title',
      permissionList: [],
    },
  },
  {
    path: '/settings',
    name: ERouteName.SETTINGS,
    component: () => import('@/page/general-settings/general-settings.vue'),
    redirect: () => ({
      name: ERouteName.SETTINGS_APPEARANCE,
    }),
    meta: {
      userManualPage: 'settings/general-settings/index.md',
      titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
      // No permissions required. User always has access to it's own user
      // settings and check for tenant settings is done within the page
      permissionList: [],
    },
    children: [
      {
        path: 'appearance',
        name: ERouteName.SETTINGS_APPEARANCE,
        component: () => import('@/page/general-settings/appearance/appearance.vue'),
        meta: {
          userManualPage: 'settings/general-settings/appearance.md',
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [],
        },
      },
      {
        path: 'pinned-apps',
        name: ERouteName.SETTINGS_PINNED_APPS,
        component: () => import('@/page/general-settings/pinned-apps/pinned-apps.vue'),
        meta: {
          userManualPage: 'settings/general-settings/pinned-apps.md',
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [],
        },
      },
      {
        path: 'api-key',
        name: ERouteName.SETTINGS_API_KEY,
        component: () => import('@/page/general-settings/api-key/api-key.vue'),
        meta: {
          userManualPage: 'settings/general-settings/api-key.md',
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [],
        },
      },
      {
        path: 'asset-parameter',
        name: ERouteName.SETTINGS_ASSET_PARAMETER,
        component: () => import('@/page/general-settings/asset-parameter-definitions/asset-parameter-definitions.vue'),
        meta: {
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [],
        },
      },
      {
        path: 'audit-trail',
        name: ERouteName.SETTINGS_AUDIT_TRAIL,
        component: () => import('@/page/general-settings/audit-trail/audit-trail.vue'),
        meta: {
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [],
        },
      },
      {
        path: 'data-sources',
        name: ERouteName.SETTINGS_DATA_SOURCES,
        component: () => import('@/page/general-settings/data-sources/data-sources.vue'),
        meta: {
          userManualPage: 'settings/general-settings/data-sources.md',
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'notification',
        name: ERouteName.SETTINGS_NOTIFICATIONS_CURRENT_USER,
        component: () => import('@/page/general-settings/notifications/notifications-current-user.vue'),
        meta: {
          userManualPage: 'settings/general-settings/notifications.md',
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [],
        },
      },
      {
        path: 'license',
        name: ERouteName.SETTINGS_LICENSE,
        component: () => import('@/page/general-settings/license/license.vue'),
        meta: {
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'meanings',
        name: ERouteName.SETTINGS_MEANINGS,
        component: () => import('@/page/general-settings/meaning/meaning.vue'),
        meta: {
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'report-builder/:reportTemplateId?',
        name: ERouteName.SETTINGS_REPORT_BUILDER,
        component: () => import('@/page/general-settings/report-builder/report-builder.vue'),
        meta: {
          userManualPage: 'settings/general-settings/report-builder.md',
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'report-builder/components/:componentId?',
        name: ERouteName.SETTINGS_REPORT_COMPONENTS,
        component: () => import('@/page/general-settings/report-builder/report-components.vue'),
        meta: {
          userManualPage: 'settings/general-settings/report-builder.md',
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'qmulus-appearance',
        name: ERouteName.SETTINGS_QMULUS_APPEARANCE,
        component: () => import('@/page/general-settings/qmulus-appearance/qmulus-appearance.vue'),
        meta: {
          userManualPage: 'settings/general-settings/qmulus-appearance.md',
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'tenant-notification-channel',
        name: ERouteName.SETTINGS_NOTIFICATIONS_CHANNEL,
        component: () => import('@/page/general-settings/notifications/notifications-channel.vue'),
        meta: {
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'tenant-notification-channel/create-email',
        name: ERouteName.SETTINGS_NOTIFICATIONS_CHANNEL_EMAIL_CREATE,
        component: () => import('@/page/general-settings/notifications/components/notification-channel/email/create.vue'),
        meta: {
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.READ,
            },
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.WRITE,
            },
          ],
        },
      },
      {
        path: 'tenant-notification-channel/edit-email/:notificationChannelId',
        name: ERouteName.SETTINGS_NOTIFICATIONS_CHANNEL_EMAIL_EDIT,
        component: () => import('@/page/general-settings/notifications/components/notification-channel/email/edit.vue'),
        meta: {
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.READ,
            },
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.WRITE,
            },
          ],
        },
      },
      {
        path: 'tenant-notification-channel/details-email/:notificationChannelId',
        name: ERouteName.SETTINGS_NOTIFICATIONS_CHANNEL_EMAIL_DETAILS,
        component: () => import('@/page/general-settings/notifications/components/notification-channel/email/details.vue'),
        meta: {
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'tenant-notification',
        name: ERouteName.SETTINGS_NOTIFICATIONS_TENANT,
        component: () => import('@/page/general-settings/notifications/notifications-tenant.vue'),
        meta: {
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'app/:appId',
        name: ERouteName.SETTINGS_APP_GENERIC,
        component: () => import('@/page/general-settings/app-tenant-settings/app-tenant-settings.vue'),
        meta: {
          userManualPage: 'settings/general-settings/index.md',
          appId: EAppId.TENANT_SETTINGS,
          titleI18nKey: `api.AppId.${EAppId.TENANT_SETTINGS}`,
          permissionList: [
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'app/material',
        name: ERouteName.MATERIAL_SETTINGS,
        component: () => import('@/page/material/material-settings.vue'),
        meta: {
          userManualPage: 'applications/materials/index.md',
          appId: EAppId.TENANT_SETTINGS,
          titleI18nKey: `api.AppId.${EAppId.MATERIALS}`,
          permissionList: [
            {
              appId: EAppId.MATERIALS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'app/orders',
        name: ERouteName.ORDERS_SETTINGS,
        component: () => import('@/page/orders/order-settings.vue'),
        meta: {
          appId: EAppId.TENANT_SETTINGS,
          titleI18nKey: `api.AppId.${EAppId.ORDERS}`,
          permissionList: [
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'app/parts',
        name: ERouteName.PART_SETTINGS,
        component: () => import('@/page/part/settings.vue'),
        meta: {
          userManualPage: 'applications/parts/index.md',
          appId: EAppId.TENANT_SETTINGS,
          titleI18nKey: `api.AppId.${EAppId.PARTS}`,
          permissionList: [
            {
              appId: EAppId.TENANT_SETTINGS,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'app/simulator/general',
        name: ERouteName.SIMULATOR_SETTINGS,
        component: () => import('@/page/simulator/simulator-settings-general.vue'),
        meta: {
          userManualPage: 'applications/simulators/index.md',
          appId: EAppId.TENANT_SETTINGS,
          titleI18nKey: `api.AppId.${EAppId.SIMULATOR}`,
          permissionList: [
            {
              appId: EAppId.SIMULATOR,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'app/simulator/material-calculation-model',
        name: ERouteName.SIMULATOR_SETTINGS_MATERIAL_CALCULATION_MODEL,
        component: () => import('@/page/simulator/simulator-settings-material-calculation-model.vue'),
        meta: {
          userManualPage: 'applications/simulators/index.md',
          appId: EAppId.TENANT_SETTINGS,
          titleI18nKey: `api.AppId.${EAppId.SIMULATOR}`,
          permissionList: [
            {
              appId: EAppId.SIMULATOR,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'app/material-calculation-model/:materialCalculationModelId',
        name: ERouteName.SIMULATOR_SETTINGS_MATERIAL_CALCULATION_MODEL_DETAILS,
        component: () => import('@/page/simulator/material-calculation-model/material-calculation-model.vue'),
        meta: {
          userManualPage: 'applications/simulators/index.md',
          appId: EAppId.TENANT_SETTINGS,
          titleI18nKey: `api.AppId.${EAppId.SIMULATOR}`,
          permissionList: [
            {
              appId: EAppId.SIMULATOR,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'app/simulator/material-calculation-model/:materialCalculationModelId/edit',
        name: ERouteName.SIMULATOR_SETTINGS_MATERIAL_CALCULATION_MODEL_EDIT,
        component: () => import('@/page/simulator/material-calculation-model/material-calculation-model.vue'),
        meta: {
          userManualPage: 'applications/simulators/index.md',
          appId: EAppId.TENANT_SETTINGS,
          titleI18nKey: `api.AppId.${EAppId.SIMULATOR}`,
          permissionList: [
            {
              appId: EAppId.SIMULATOR,
              permission: EAppPermission.READ,
            },
            {
              appId: EAppId.SIMULATOR,
              permission: EAppPermission.WRITE,
            },
          ],
        },
      },
      {
        path: 'app/simulator/material-calculation-model/create',
        name: ERouteName.SIMULATOR_SETTINGS_MATERIAL_CALCULATION_MODEL_CREATE,
        component: () => import('@/page/simulator/material-calculation-model/material-calculation-model.vue'),
        meta: {
          userManualPage: 'applications/simulators/index.md',
          appId: EAppId.TENANT_SETTINGS,
          titleI18nKey: `api.AppId.${EAppId.SIMULATOR}`,
          permissionList: [
            {
              appId: EAppId.SIMULATOR,
              permission: EAppPermission.READ,
            },
            {
              appId: EAppId.SIMULATOR,
              permission: EAppPermission.WRITE,
            },
          ],
        },
      },
      {
        path: 'app/simulator/quenching-media',
        name: ERouteName.SIMULATOR_SETTINGS_QUENCHING_MEDIA,
        component: () => import('@/page/simulator/simulator-settings-quenching-media.vue'),
        meta: {
          userManualPage: 'applications/simulators/index.md',
          appId: EAppId.TENANT_SETTINGS,
          titleI18nKey: `api.AppId.${EAppId.SIMULATOR}`,
          permissionList: [
            {
              appId: EAppId.SIMULATOR,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'app/quality',
        name: ERouteName.CERTIFICATION_QUALITY_SETTINGS,
        component: () => import('@/page/quality/settings.vue'),
        meta: {
          userManualPage: 'applications/audit-quality/quality.md',
          appId: EAppId.TENANT_SETTINGS,
          titleI18nKey: `api.AppId.${EAppId.CERTIFICATION}`,
          permissionList: [
            {
              appId: EAppId.CERTIFICATION,
              permission: EAppPermission.READ,
            },
          ],
        },
      },
      {
        path: 'release-notes',
        name: ERouteName.RELEASE_NOTES,
        component: () => import('@/page/general-settings/about/release-notes.vue'),
        meta: {
          titleI18nKey: 'page.releaseNotes.title',
          permissionList: [],
        },
      },
      {
        path: 'release-highlights',
        name: ERouteName.RELEASE_HIGHLIGHTS,
        component: () => import('@/page/general-settings/about/release-highlights.vue'),
        meta: {
          titleI18nKey: 'page.releaseHighlights.title',
          permissionList: [],
        },
      },
      {
        path: 'opensource-licenses',
        name: ERouteName.OPENSOURCE_LICENSES,
        component: () => import('@/page/general-settings/about/open-source-licenses.vue'),
        meta: {
          titleI18nKey: 'page.openSourceLicenses.title',
          permissionList: [],
        },
      },
    ],
  },
  {
    path: '/dashboard',
    name: ERouteName.QMULUS_DASHBOARD,
    component: () => import('@/page/qmulus-dashboard/qmulus-dashboard.vue'),
    meta: {
      appId: EAppId.QMULUS_DASHBOARD,
      titleI18nKey: `api.AppId.${EAppId.QMULUS_DASHBOARD}`,
      permissionList: [
        {
          appId: EAppId.QMULUS_DASHBOARD,
          permission: 'Read',
        },
      ],
    },
    children: [
      {
        path: '/dashboard/:assetId',
        name: ERouteName.QMULUS_DASHBOARD_ASSET,
        component: () => import('@/page/qmulus-dashboard/qmulus-dashboard-asset.vue'),
        meta: {
          appId: EAppId.QMULUS_DASHBOARD,
          titleI18nKey: `api.AppId.${EAppId.QMULUS_DASHBOARD}`,
          permissionList: [
            {
              appId: EAppId.QMULUS_DASHBOARD,
              permission: 'Read',
            },
          ],
        },
      },
    ],
  },
  {
    path: '/orders',
    name: ERouteName.ORDERS,
    component: () => import('@/page/orders/order-overview.vue'),
    meta: {
      appId: EAppId.ORDERS,
      titleI18nKey: `api.AppId.${EAppId.ORDERS}`,
      permissionList: [
        {
          appId: EAppId.ORDERS,
          permission: 'Read',
        },
      ],
    },
    children: [],
  },
  {
    path: '/orders/:orderId/:revisionId',
    name: ERouteName.ORDERS_DETAIL,
    component: () => import('@/page/orders/order-details.vue'),
    meta: {
      appId: EAppId.ORDERS,
      titleI18nKey: `api.AppId.${EAppId.ORDERS}`,
      permissionList: [
        {
          appId: EAppId.ORDERS,
          permission: 'Read',
        },
      ],
    },
    children: [],
  },
  {
    path: '/production-scheduler',
    name: ERouteName.PRODUCTION_SCHEDULER,
    component: () => import('@/page/production-scheduler/production-scheduler.vue'),
    meta: {
      appId: EAppId.PRODUCTION_SCHEDULER,
      titleI18nKey: `api.AppId.${EAppId.PRODUCTION_SCHEDULER}`,
      permissionList: [
        {
          appId: EAppId.PRODUCTION_SCHEDULER,
          permission: 'Read',
        },
      ],
    },
    children: [],
  },
  {
    name: ERouteName.DEBUG_TOOLS,
    path: '/debug-tools',
    component: () => import('@/page/debug-tools/debug-tools.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: ERouteName.NOT_FOUND,
    component: () => import('@/page/not-found/not-found.vue'),
    meta: {
      titleI18nKey: 'page/not-found/not-found.title',
      permissionList: [],
    },
  },
];

// used in e2e tests
export const memoryRouter = createRouter({
  routes,
  history: createMemoryHistory(),
});
